import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Megaphone, ArrowRight, BarChart3 } from 'lucide-react';
import { API_BASE_URL as API } from '../config/api';
import { motion } from 'framer-motion';

export default function AdSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const campaignId = params.get('campaign_id');
  const [status, setStatus] = useState('checking');
  const [campaignTitle, setCampaignTitle] = useState('');

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let attempts = 0;
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/ads/campaign-status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          setCampaignTitle(data.title || '');
          if (data.payment_status === 'paid' || data.status === 'active') {
            setStatus('success');
            clearInterval(poll);
          }
        }
      } catch {}
      attempts++;
      if (attempts > 20) { setStatus('success'); clearInterval(poll); }
    }, 2000);
    return () => clearInterval(poll);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="ad-success-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <Card className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-orange-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Processing Payment...</h2>
              <p className="text-muted-foreground">Activating your ad campaign</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold mb-2">Ad Campaign Live!</h2>
              <p className="text-muted-foreground mb-1">
                {campaignTitle ? `"${campaignTitle}" is now running` : 'Your ad is now running'}
              </p>
              <p className="text-xs text-muted-foreground mb-6">Your ad will start showing to users immediately</p>
              <div className="space-y-2">
                <Button onClick={() => navigate('/ads/manager')} className="w-full bg-orange-500 hover:bg-orange-600">
                  <BarChart3 className="w-4 h-4 mr-2" /> View Ad Manager
                </Button>
                <Button variant="outline" onClick={() => navigate('/ads/create')} className="w-full">
                  <Megaphone className="w-4 h-4 mr-2" /> Create Another Ad
                </Button>
              </div>
            </>
          )}
          {status === 'error' && (
            <>
              <h2 className="text-xl font-bold mb-2">Something Went Wrong</h2>
              <p className="text-muted-foreground mb-4">We couldn't verify your payment</p>
              <Button onClick={() => navigate('/ads/manager')} className="w-full">Go to Ad Manager</Button>
            </>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
